import type { NumOrNull, Primitive } from "@/types/common";
import type { ChartPivotRow, ResultSet } from "@cubejs-client/core";
import { format } from "date-fns";
import { USER_DATE_FORMAT_WITH_TIME } from "@/constants/date.constants";
import logger from "@/helpers/logger";

export type CubeError = {
  message: string;
  status?: number;
  type?: string;
  error?: string;
};

type RawDataRow<D = string | number | boolean | null> = Record<string, D>;

export type DimensionAccessor = <T extends Primitive = Primitive>(
  row: ChartPivotRow,
  dimensionKey: string,
  /** Fallback is optional as the function will use sane defaults for the inferred type */
  fallback?: T
) => T;

export const handleCubeError = (error: CubeError) => {
  // Log the error
  logger.error("Cube query error:", {
    message: error.message,
    status: error.status,
    type: error.type,
    error: error.error,
  });

  // Return a user-friendly error message
  switch (error.status) {
    case 400:
      return "Bad request. Please try again.";
    case 401:
      return "Authentication error. Please try logging in again.";
    case 403:
      return "You don't have permission to access this data.";
    case 404:
      return "The requested data could not be found.";
    case 500:
      return "Server error. Please try again later.";
    default:
      return error.message || "An error occurred while fetching data.";
  }
};

export const isCubeError = (error: unknown): error is CubeError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as CubeError).message === "string"
  );
};

/**
 * Accumulates two numeric values, returning their sum, or null if either value is null.
 *
 * @param acc - The accumulator value (number or null, defaults to 0).
 * @param val - The value to add to the accumulator (number or null, defaults to 0).
 * @returns The sum of acc and val, or null if either is null.
 */
const sumAccumulator = (acc: NumOrNull = 0, val: NumOrNull = 0): NumOrNull => {
  const numVal = Number(val) || 0;
  return acc === null || val === null ? null : acc + numVal;
};

/**
 * Accumulates two numeric values, returning their sum, or null if value is null or zero.
 *
 * NP-689 This is a workaround for Cube queries that return zero values when null is intended
 *
 * @param acc - The accumulator value (number or null, defaults to 0).
 * @param val - The value to add to the accumulator (number or null, defaults to 0).
 * @returns The sum of acc and val, or null if null or zero.
 */
export const zeroToNullSum = (
  acc: NumOrNull = 0,
  val: NumOrNull = 0
): NumOrNull => {
  const numVal = Number(val) || 0;
  return acc === null || !numVal ? null : acc + numVal;
};

/**
 * Flattens an array of ChartPivotRow objects by aggregating measure values using the provided accumulator function.
 *
 * @param data - Array of chart pivot rows to flatten.
 * @param [accumulator] - Function to aggregate measure values (default: sum).
 * @returns A single ChartPivotRow with aggregated measure values.
 */
export const flattenChartPivotData = (
  data: ChartPivotRow[] = [],
  accumulator = sumAccumulator
): ChartPivotRow => {
  return data.reduce<ChartPivotRow>(
    (acc, row) => {
      const { x, xValues, ...measures } = row;

      Object.entries<number>(measures).forEach(([key, value]) => {
        const currValue = acc[key] ?? 0;
        acc[key] = accumulator(currValue, value);
      });

      return acc;
    },
    { x: "", xValues: [""] }
  );
};

/**
 * Flattens an array of RawDataRow objects by aggregating numeric values using the provided accumulator function.
 *
 * @param data - Array of raw data rows to flatten.
 * @param excludeCols - Array of column suffixes to exclude from aggregation.
 * @param accumulator - Function to aggregate values (default: sum).
 * @returns A single RawDataRow with aggregated values for each column.
 */
export const flattenRawData = (
  data: RawDataRow[] = [],
  excludeCols: string[] = [],
  accumulator = sumAccumulator
) => {
  return data.reduce<RawDataRow<NumOrNull>>((acc, row) => {
    Object.entries<NumOrNull>(row as RawDataRow<NumOrNull>).forEach(
      ([key, value]) => {
        const excluded = excludeCols.some((str) => key.endsWith(str));
        if (excluded) return;

        acc[key] = accumulator(acc[key], value);
      }
    );
    return acc;
  }, {});
};

/**
 * Creates a type-safe accessor function that maps dimension names to their values from a ChartPivotRow.
 * Uses the query's dimensions array from resultSet.query() to determine the correct order,
 * which is guaranteed to match the xValues array order.
 *
 * **Important**: Only timeDimensions WITH `granularity` appear in xValues. TimeDimensions without
 * granularity (only `dateRange`) act as WHERE clause filters and are NOT included in result rows.
 *
 * Automatically casts dimension values based on the generic type parameter and annotation metadata:
 * - Type parameter `T` on the accessor call determines the return type (defaults to Primitive)
 * - Falls back to annotation type info when available
 * - Performs runtime casting to match the type signature
 *
 * @param resultSet - The Cube.js ResultSet containing the original query
 * @param debug - If true then logs the dimensions it has identified
 * @returns A DimensionAccessor function that takes a row, dimension key, and optional fallback value with a type parameter
 *
 * @see {@link file://../../docs/CUBEJS_DIMENSIONS_ANALYSIS.md} for detailed analysis of Cube.js dimension behavior
 *
 * @example
 * // Single accessor for all types
 * const getDimension = createDimensionAccessor(resultSet);
 * const marketName = getDimension<string>(row, "Market.name", ""); // Type: string
 * const duration = getDimension<number>(row, "TrueDurationHours", 0); // Type: number
 * const value = getDimension(row, "SomeField"); // Type: Primitive (default)
 */
export const createDimensionAccessor = (
  resultSet: ResultSet | null | undefined,
  debug = false
): DimensionAccessor => {
  if (!resultSet) {
    return <T extends Primitive = Primitive>(
      _row: ChartPivotRow,
      _dimensionKey: string,
      fallback?: T
    ): T => fallback ?? ("" as T);
  }

  const dimensionMap = new Map<string, number>();
  const typeMap = new Map<string, string>();

  try {
    // Get the dimensions array from the original query
    // This array order matches the xValues array order
    const query = resultSet.query();
    const dimensions = query?.dimensions || [];
    const timeDimensions = query?.timeDimensions || [];

    // Safely get annotations (may not exist in all contexts/mocks)
    const annotations =
      typeof resultSet.annotation === "function"
        ? resultSet.annotation()
        : undefined;

    // Build a combined array of all dimension keys
    // TimeDimensions come FIRST in xValues, then regular dimensions
    // NOTE: Based on observed behavior (not explicitly documented in Cube.js docs),
    // only timeDimensions WITH granularity are included in xValues.
    // TimeDimensions with only dateRange act as WHERE clause filters and don't
    // contribute dimension values to result rows, so they're excluded from the mapping.
    const allDimensions: string[] = [
      ...timeDimensions
        .filter((td) => td.granularity)
        .map((td) => td.dimension),
      ...dimensions,
    ];

    if (allDimensions.length > 0) {
      allDimensions.forEach((dimensionKey, index) => {
        dimensionMap.set(String(dimensionKey), index);

        // Store the type from annotations for automatic casting
        const dimType = annotations?.dimensions?.[dimensionKey]?.type;
        if (dimType) {
          typeMap.set(String(dimensionKey), dimType);
        }
      });
    }
  } catch (error) {
    logger.warn("Failed to create dimension accessor from resultSet", {
      error,
    });
  }

  // If no dimensions were mapped, return null to signal that positional access should be used
  if (dimensionMap.size === 0) {
    logger.warn(`Cannot find dimension data for resultSet`);
  }

  if (debug) {
    logger.log(
      "createDimensionAccessor maps:",
      Array.from(dimensionMap.keys()),
      Array.from(typeMap.values())
    );
  }

  return <T extends Primitive = Primitive>(
    row: ChartPivotRow,
    dimensionKey: string,
    fallback?: T
  ): T => {
    const index = dimensionMap.get(dimensionKey);
    const dimType = typeMap.get(dimensionKey);

    // Helper to get type-appropriate default when no fallback provided
    const getTypeDefault = (): T => {
      if (fallback !== undefined) return fallback;

      // Use annotation type to determine appropriate default
      if (dimType === "number") return 0 as T;
      if (dimType === "string" || dimType === "time") return "" as T;
      if (dimType === "boolean") return false as T;

      // Default to empty string for unknown types
      return "" as T;
    };

    if (index !== undefined) {
      const rawValue = row.xValues?.[index];

      // If value is null/undefined, use type-appropriate fallback
      if (rawValue == null) {
        return getTypeDefault();
      }

      // Cast based on annotation type or inferred from fallback type
      // Use annotation type if available
      if (dimType === "number") {
        return Number(rawValue) as T;
      } else if (dimType === "boolean") {
        return (String(rawValue) === "true") as T;
      } else if (dimType === "string" || dimType === "time") {
        return String(rawValue) as T;
      }

      // Fallback to inference: if fallback is a number, cast to number
      if (typeof fallback === "number") {
        return Number(rawValue) as T;
      } else if (typeof fallback === "string") {
        return String(rawValue) as T;
      }

      // No type info - return as-is and cast
      return rawValue as T;
    }
    return getTypeDefault();
  };
};

/**
 * Extracts the latest refresh time from a Cube.js ResultSet and formats it as a subtitle.
 * Compares all lastRefreshTime values across query results and returns a formatted string.
 *
 * @deprecated not used - lastRefreshTimes from Cube does not reflect actual update date see NP-1044.
 *
 * @param resultSet - The Cube.js ResultSet containing query results
 * @returns A formatted subtitle string like "Updated 15/01/2024 10:30", or undefined if no results
 */
export const getLatestRefreshTime = (
  resultSet: ResultSet | null | undefined
): string | null => {
  if (!resultSet) {
    return null;
  }

  // lastRefreshTime is only public on serialized data
  const lastRefreshTimes = resultSet
    .serialize()
    .loadResponse.results.map((r) => r.lastRefreshTime);

  if (lastRefreshTimes.length === 0) {
    return null;
  }

  const latestRefreshTime = new Date(
    Math.max(...lastRefreshTimes.map((t) => +new Date(t)))
  );

  return `Updated ${format(latestRefreshTime, USER_DATE_FORMAT_WITH_TIME)}`;
};
